<template>
	<div class="flex flex-col h-full">
        <div class="w-full building-title-bar border-b-2 border-teal-700 shadow-lg py-3">
            <div class="container mx-auto flex items-center justify-between w-full">
                <div class="group flex flex-col text-white leading-normal hover:text-blue-100">
                    <div @click="showBuildings" class="flex cursor-pointer">
                        <div>
                            <div class="text-xl font-semibold uppercase">{{ building.address_line_1 }}</div>
                            <div class="text-sm uppercase">{{ building.city }}, {{ building.state }}</div>
                        </div>
                        <div class="flex">
                            <inline-svg name="chevron-down" classes="fill-current text-white group-hover:text-blue-100 h-5 w-5 ml-3 cursor-pointer"></inline-svg>
                        </div>
                    </div>
                </div>
                <div v-if="$gate.isAdmin()">
                    <div class="group/dropdown dropdown px-4 py-2 bg-gray-50 rounded-md border border-gray-200">
                        <div class="flex items-center">
                            <span class="text-gray-600 group-hover/dropdown:text-gray-500 font-semibold">Manage Building</span>
                            <inline-svg name="chevron-down" classes="ml-1 fill-current text-gray-500 w-6 h-6"></inline-svg>
                        </div>
                        <div class="dropdown-menu">
                            <ul>
                                <li v-if="$gate.isAdmin()" class="group space-x-2 hover:bg-gray-100">
                                    <inline-svg name="trash" classes="text-gray-300 group-hover:text-red-500 fill-current w-4 h-4"></inline-svg>
                                    <button @click="confirmDeleteBuilding" type="button" class="text-base text-gray-500">Delete Building</button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex-1 overflow-y-auto">
            <div class="py-5 mx-auto container">
                <a :href="`/sites/${fromSiteId}`" v-if="fromSiteId" class="flex items-center gap-x-2 mb-5 text-sm font-semibold hover:cursor-pointer">
                    <inline-svg name="arrow-thin-right" classes="w-3 h-3 text-blue-500 rotate-180" />
                    Back to Site
                </a>
                <div class="flex mb-5">
                    <div class="flex-1 mr-4 overflow-hidden">
                        <media-gallery
                            :documentable-id="building.id"
                            documentable-type="App\Building"
                            :media="building.media"
                            :height="500">
                        </media-gallery>
                    </div>
                    <div class="w-2/5 shadow-md">
                        <map-component
                            :latitude="building.latitude"
                            :longitude="building.longitude"
                            :zoom="6">
                        </map-component>
                    </div>
                </div>

                <nav class="mt-6 -mb-px border-b flex space-x-2" aria-label="Tabs">
                    <button
                        v-for="(tab, slug) in enabledTabs" :key="slug"
                        @click="selectedTab = slug"
                        class="py-2 px-3 border border-gray-200 flex whitespace-nowrap rounded-t-lg text-sm"
                        :class="slug != selectedTab ? tab.classes : tab.selectedClasses">
                            {{ tab.label }}
                        <span
                            v-if="tab.showCount"
                            class="py-0.5 px-2.5 ml-3 hidden rounded-full text-xs font-medium md:inline-block"
                            :class="{ 'bg-gray-200 text-gray-900': slug != selectedTab, 'bg-blue-800 text-white': slug == selectedTab }">
                                {{ tab.count }}
                        </span>
                    </button>
                </nav>

                <div class="p-5 mb-6 border-l">
                    <div class="mt-2">
                        <edit-address v-if="selectedTab == 'address'" :building-data="building"></edit-address>

    		            <edit-building-type v-if="selectedTab == 'type'" :building-data="building"></edit-building-type>

    		            <edit-building-details v-if="selectedTab == 'details'" :building-data="building"></edit-building-details>

    	                <units v-if="selectedTab == 'units'" show-edit-actions></units>

                        <contacts
                            v-if="selectedTab == 'contacts'"
                            :contact-data="building.contacts"
                            attach-to="building"
                            :attach-to-id="building.id">
                        </contacts>

                        <documents
                            v-if="selectedTab == 'documents'"
                            :document-data="building.documents"
                            :documentable-id="building.id"
                            documentable-type="App\Building">
                        </documents>

                        <building-danger-zone
                            v-if="selectedTab == 'dangerZone'"
                            :building-data="building"
                            :view-only-mode="false">
                        </building-danger-zone>
                    </div>
                    <button v-if="showSaveButton" @click="saveBuilding" type="button" class="mt-8 btn btn-green">Save Building</button>
    			</div>
            </div>
		</div>

        <notifications classes="notification" position="bottom right"></notifications>

        <modal @opened="$eventHub.$emit('convert-currency')" name="building-cards" width="1000px" height="auto" click-to-close scrollable adaptive>
            <div class="p-8">
                <building-cards
                    view="table"
                    :per-page="7"
                    :show-create-new="false"
                    :show-client-info="false"
                    small-header
                    center-cards>
                </building-cards>
            </div>
        </modal>
	</div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
    	props: {
    		buildingData: {
    			type: Object,
    			required: true
    		},

            openTab: {
                type: String,
                default: 'address'
            },

            fromSiteId: {
                type: Number,
                required: false
            }
    	},

    	data() {
    		return {
                selectedTab: this.openTab,
                tabClasses: 'bg-gray-50 text-gray-500 font-medium hover:border-gray-200 hover:text-gray-700',
                selectedTabClasses: 'bg-gradient-to-br from-blue-500 to-blue-700 font-bold text-white'
    		}
    	},

        created() {
            this.buildings.selectedBuilding = this.buildingData;
            this.documents.all = this.buildingData.documents;
            this.documents.filtered = this.buildingData.documents;
            this.units.all = this.buildingData.units;
            this.contacts.all = this.buildingData.contacts;
            this.contacts.filtered = this.buildingData.contacts;
        },

        computed: {
            enabledTabs() {
                // v-for used to be paired with v-if="tab.enabled"; Vue 2 runs
                // v-for first so the list was already filtered. Same result, and
                // it does not break when the directive priority reverses in Vue 3.
                const source = this.tabs;
                return Array.isArray(source)
                    ? source.filter(entry => entry.enabled)
                    : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
            },

            ...mapState(['buildings']),
            ...mapState(['documents']),
            ...mapState(['units']),
            ...mapState(['contacts']),

            building() {
                return this.buildings.selectedBuilding;
            },

            showSaveButton() {
                return this.tabs[this.selectedTab].showSaveButton;
            },

            tabs() {
                return {
                    address: {
                        label: "Address",
                        enabled: true,
                        showCount: false,
                        showSaveButton: true,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    type: {
                        label: "Building Type",
                        enabled: true,
                        showCount: false,
                        showSaveButton: true,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    details: {
                        label: "Details",
                        enabled: true,
                        showCount: false,
                        showSaveButton: true,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    units: {
                        label: "Units",
                        enabled: true,
                        showCount: true,
                        count: this.building.units.length,
                        showSaveButton: false,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    contacts: {
                        label: "Contacts",
                        enabled: true,
                        showCount: true,
                        count: this.contacts.all.length,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    documents: {
                        label: "Documents",
                        enabled: true,
                        showCount: true,
                        count: this.documents.all.length || 0,
                        showSaveButton: false,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    dangerZone: {
                        label: "Danger Zone",
                        enabled: true,
                        showCount: false,
                        showSaveButton: true,
                        classes: this.building.is_in_danger_zone ? 'bg-gradient-to-br from-red-500 to-red-700 font-bold text-white' : this.tabClasses,
                        selectedClasses: this.building.is_in_danger_zone ? 'bg-gradient-to-br from-red-400 to-red-700 font-bold text-white' : this.tabClasses
                    },
                }
            }
        },

        methods: {
            showBuildings() {
                this.$modal.show('building-cards');
            },

            saveBuilding() {
                axios.patch('/buildings/' + this.building.id, this.building).then(response => {
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: 'Building Saved'
                    });
                }).catch(error => {
                    console.error(error);
                });
            },

            confirmDeleteBuilding() {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this building?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteBuilding();
                    }
                });
            },

            deleteBuilding() {
                axios.delete('/buildings/' + this.building.id)
                    .then(response => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Building Deleted'
                        });

                        setTimeout(() => {
                            window.location = '/admin/buildings'
                        }, 1000);
                    })
                    .catch(error => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    }
                );
            }
        }
    }
</script>
